// CategoryNavBar.tsx — horizontal category nav bar
import React from 'react';
import { Box, Chip, Skeleton } from '@mui/material';

interface Category {
  id: string;
  name: string;
  slug: string;
  icon?: string;
  service_count: number;
}

interface CategoryNavBarProps {
  categories: Category[];
  activeCategory: string;
  onCategoryChange: (slug: string) => void;
  loading?: boolean;
}

export function CategoryNavBar({ categories, activeCategory, onCategoryChange, loading }: CategoryNavBarProps) {
  const totalCount = categories.reduce((s, c) => s + (c.service_count || 0), 0);
  const all = [
    { id: 'all', name: 'All Services', slug: '', service_count: totalCount, icon: '' },
    ...categories,
  ];

  return (
    <Box
      sx={{
        width: '100%',
        mb: 2,
        position: 'relative',
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, pb: 0.5, minWidth: 'max-content', flexWrap: 'nowrap' }}>
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" width={100} height={32} />
            ))
          : all.map((cat) => {
              const isActive =
                cat.slug === activeCategory || (cat.slug === '' && !activeCategory);
              return (
                <Chip
                  key={cat.id}
                  label={cat.name}
                  clickable
                  size="small"
                  variant={isActive ? 'filled' : 'outlined'}
                  color={isActive ? 'primary' : 'default'}
                  onClick={() => onCategoryChange(cat.slug)}
                  sx={{
                    fontWeight: isActive ? 700 : 400,
                    fontSize: '0.8rem',
                    transition: 'all 0.15s ease',
                    whiteSpace: 'nowrap',
                  }}
                />
              );
            })}
      </Box>
    </Box>
  );
}
